import {
  // SearchApiModel,
  useAxios,
  // useDeleteNulls,
  useHandleApiError,
} from '@sttedra_ui/framework';
import {stringEndsWithNumber} from '@/util.js';
import {LAYERS_GROUP_TREE} from './layer-tree-group.js';

// #region Globals
const {base} = useAxios();
// const CONTEXT_BASE_PATH = '';
const DEFAULT_TIMEOUT = 5000;
const {handleApiError} = useHandleApiError();
const URLS3REPLACE = '{{SERVER_S3}}';
const URLTMSREPLACE = '{{SERVER_TMS}}';
const PREVIEW_SUFFIX_PLACEHOLDER = '{{PREVIEW_SUFFIX}}';

// metadata layer structure in STYLE_VECTOR:
// - description: string
// - iconName: string // Icon name from GEOSERVER
// - alwaysVisible: boolean
// - selectorShown: boolean // false if alwaysVisible
// - selectorLinkedLayers: string[]
//   ^^^^ List of layers that should mimic selector behaviour to this one
const defaultMetadataBaseLayer = () => {
  return {
    description: '',
    iconName: '',
    alwaysVisible: true,
    selectorShown: false,
    selectorLinkedLayers: [],
  };
};
const defaultLayoutBaseLayer = () => {
  return {
    visibility: 'visible',
  };
};
const defaultLayoutLayer = () => {
  return {
    visibility: 'none',
  };
};
const defaultMetadataLayer = ({
  id,
}) => {
  return {
    description: id,
    iconName: '',
    alwaysVisible: false,
    selectorShown: true,
    selectorLinked: [],
  };
};
// #endregion
// #region Private functions
function getLinkedLayersByNameConvention(styleVector, layerName) {
  const layerIds = styleVector.layers.
    filter((l) => !l?.metadata?.alwaysVisible).
    map((l) => l.id);
  const strRegExp = layerName + '\\d+';
  const layerRegExp = new RegExp(strRegExp);
  const linkedLayers = layerIds.filter((id) => layerRegExp.test(id));
  return linkedLayers;
}
function addMetadataLayoutStyleJson(styleVector) {
  const newStyleVector = {...styleVector};
  const MAPA_BASE_SOURCE = 'MapaBaseVT';
  const hideLayers = [];
  const needsTreeGroupMetadata = !newStyleVector?.metadata?.mitmaLayerTree?.length;

  if (needsTreeGroupMetadata) {
    newStyleVector.metadata.mitmaLayerTree = LAYERS_GROUP_TREE;
  }

  for (const l of newStyleVector.layers) {
    const isBaseLayer = l.source === MAPA_BASE_SOURCE;
    const hasMetadata = !!l?.metadata ?? false;
    const hasIconName = !!l?.metadata?.iconName;
    const hasLayout = !!l?.layout ?? false;
    const hasVisibility = !!l?.layout?.visibility ?? false;
    const hasAscendants = hasMetadata && !!l?.metadata?.ascendants;
    const needsSelectorLinked = hasMetadata && !l?.metadata?.alwaysVisible &&
          !stringEndsWithNumber(l.id) &&
      (!l?.metadata?.selectorLinked || !l?.metadata?.selectorLinked?.length);
    // Add new field metadata
    if (hasMetadata && !hasIconName) {
      l.metadata.iconName = l.id;
    }
    // Delete ascendants
    if (hasAscendants) {
      delete l.metadata.ascendants;
    }
    if (isBaseLayer) {
      if (!hasMetadata) {
        l.metadata = defaultMetadataBaseLayer();
      }
      if (!hasLayout) {
        l.layout = defaultLayoutBaseLayer();
      }
      if (hasLayout && !hasVisibility) {
        l.layout.visibility = 'visible';
      }
    } else {
      if (!hasMetadata) {
        l.metadata = defaultMetadataLayer(l);
      }
      if (needsSelectorLinked) {
        const linkedLayers = getLinkedLayersByNameConvention(styleVector, l.id);
        hideLayers.push(...linkedLayers);
        l.metadata.selectorLinked = linkedLayers;
      }
      if (!hasLayout) {
        l.layout = defaultLayoutLayer();
      }
      if (hasLayout && !hasVisibility) {
        l.layout.visibility = 'none';
      }
    }
  }
  // Hide linked Layers
  for (const lId of hideLayers) {
    const foundLayer = newStyleVector.layers.find((l) => l.id === lId);
    foundLayer.metadata.selectorShown = false;
  }

  return newStyleVector;
}
function processTreeNode(node, layers, rasterLayers) {
  node.iconName = '';
  node.layerName = null;
  node.layerOrder = -1;
  node.alwaysVisible = true;
  node.selectorShown = true;
  node.selectorLinked = [];
  if (node.level > 1) {
    if (!!node.children && !!node.children?.length) {
      let visible = true;
      for (const n of node.children) {
        processTreeNode(n, layers, rasterLayers);
        visible = visible && n?.visible;
      }
      node.visible = visible;
      node.defaultVisible = visible;
    }
    return;
  }
  if (node.level === 1 && !!node?.children?.length) {
    const newChildren = [];
    let visible = true;
    for (const layer of node.children) {
      if (!!layer?.rasterInfo?.id) {
        visible = visible && !!layer?.visible;
        // Raster layer
        rasterLayers.push({...layer, rasterLayer: true});
        newChildren.push({...layer, rasterLayer: true});
      } else {
        // Vector layer
        const layerId = layer;
        const found = layers.find((l) => l.id === layerId);
        if (!!found) {
          visible = visible && !!found?.visible;
          newChildren.push({...found, rasterLayer: false});
          layers = layers.filter((l) => l.id !== found.id);
        }
      }
    }
    node.children = newChildren;
    node.visible = visible;
    node.defaultVisible = visible;
  }
}
function convertLayerTreeFromMaplibreStyle(layerTree, allLayers) {
  // Deep copy
  const newLayerTree = JSON.parse(JSON.stringify(layerTree));
  const layers = allLayers.map((l) => l);
  const rasterLayers = [];
  for (const node of newLayerTree) {
    processTreeNode(node, layers, rasterLayers);
  }
  return {newLayerTree, rasterLayers};
}
function convertLayersFromMaplibreStyle(styleJson) {
  let currentOrder = 1;
  const layers = [];
  const styleVector = addMetadataLayoutStyleJson(styleJson);
  for (const l of styleVector.layers) {
    const id = l.id;
    const description = l?.metadata?.description ?? l.id;
    const iconName = l?.metadata?.iconName ?? null;
    const layerName = l.id;
    const layerOrder = currentOrder;
    const defaultVisible = l?.layout?.visibility === 'visible' ? true : false;
    const alwaysVisible = l?.metadata?.alwaysVisible ?? false;
    const selectorShown = l?.metadata?.selectorShown ?? false;
    const selectorLinked = l?.metadata?.selectorLinked ?? [];
    const children = null;
    const newLayer = {
      id,
      description,
      iconName,
      layerName,
      layerOrder,
      defaultVisible,
      alwaysVisible,
      selectorShown,
      selectorLinked,
      children,
      visible: defaultVisible,
    };
    layers.push(newLayer);
    currentOrder++;
  }
  const {newLayerTree, rasterLayers} = convertLayerTreeFromMaplibreStyle(
    styleVector.metadata.mitmaLayerTree,
    layers);
  const featureLayers = !!styleVector?.metadata?.featureLayers ?
    styleVector.metadata.featureLayers : [];
  return {layerTree: newLayerTree, rasterLayers, featureLayers};
}
// #endregion
// #region Public functions
async function fetchAndProcessStyleJson(url, urlS3, tmsServer, previewSuffix = '') {
  try {
    const {data: rawJson} = await base.get(url, {timeout: DEFAULT_TIMEOUT, baseURL: '/'});
    const stringJson = JSON.stringify(rawJson)
      .replaceAll(URLS3REPLACE, urlS3)
      .replaceAll(URLTMSREPLACE, tmsServer)
      .replaceAll(PREVIEW_SUFFIX_PLACEHOLDER, previewSuffix);

    return JSON.parse(stringJson);
  } catch (err) {
    return handleApiError(err);
  }
}

async function getVectorOverviewMaplibreStyle(urlS3, tmsServer, previewSuffix) {
  const url = 'statics/json/style-vector-overviewmap.json';
  return fetchAndProcessStyleJson(url, urlS3, tmsServer, previewSuffix);
}

async function getVectorMaplibreStyle(urlS3, tmsServer, previewSuffix) {
  const url = 'statics/json/style-vector.json';
  return fetchAndProcessStyleJson(url, urlS3, tmsServer, previewSuffix);
}

function getVectorLayers(styleJson) {
  try {
    const data = convertLayersFromMaplibreStyle(styleJson);
    return data;
  } catch (err) {
    return handleApiError(err);
  }
}
// #endregion

// #region Private functions
// ...
// #endregion

export {
  getVectorOverviewMaplibreStyle,
  getVectorMaplibreStyle,
  getVectorLayers,
};
